import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Loader2, Eye, EyeOff, CalendarCheck, ArrowRight, Sparkles,
  ShieldCheck, Wallet, MessageSquare, BarChart3, Utensils,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth, AuthUser } from "../lib/auth";
import { rutaInicio } from "../lib/permisos";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "Mínimo 6 caracteres"),
});
type LoginForm = z.infer<typeof loginSchema>;

interface OrgInfo {
  nombre: string;
  logoUrl?: string | null;
  colorPrimario?: string;
  imagenFondoUrl?: string | null;
}

function updatePWAManifest(nombre: string, accent: string, logoUrl?: string | null) {
  try {
    const icons = logoUrl
      ? [{ src: logoUrl, sizes: "any", type: "image/jpeg", purpose: "any maskable" }]
      : [{ src: "/icon-192.png", sizes: "192x192", type: "image/png", purpose: "any maskable" }];
    const manifest = {
      name: nombre, short_name: nombre.split(" ")[0],
      description: `Sistema de reservas — ${nombre}`,
      start_url: "/", display: "standalone",
      background_color: "#0a0a0f", theme_color: accent,
      orientation: "portrait-primary", icons, lang: "es",
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const existing = document.querySelector("link[rel='manifest']");
    if (existing) existing.setAttribute("href", url);
    const tc = document.getElementById("theme-color") as HTMLMetaElement | null;
    if (tc) tc.content = accent;
    const at = document.querySelector("meta[name='apple-mobile-web-app-title']") as HTMLMetaElement | null;
    if (at) at.content = nombre.split(" ")[0];
    document.title = nombre;
  } catch { /* no-op */ }
}

export function LoginPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [serverError, setServerError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [pendientes, setPendientes] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/v1/reservas/reservar-info")
      .then(r => r.json())
      .then(d => {
        const nombre = d.nombre ?? "";
        const aplicar = (logoUrl: string | null, colorPrimario: string, imagenFondoUrl: string | null = null) => {
          const info: OrgInfo = { nombre, logoUrl, colorPrimario, imagenFondoUrl };
          setOrgInfo(info);
          if (logoUrl) {
            const fav = document.getElementById("favicon") as HTMLLinkElement | null;
            if (fav) { fav.href = logoUrl; fav.type = "image/jpeg"; }
            const apple = document.getElementById("apple-touch-icon") as HTMLLinkElement | null;
            if (apple) apple.href = logoUrl;
          }
          updatePWAManifest(nombre, colorPrimario, logoUrl);
        };

        // Identidad del login a nivel grupo/organización (ej. Robusta con varias
        // sucursales): si está seteada, tiene prioridad y NO hereda de ninguna
        // sucursal en particular.
        if (d.loginLogoUrl || d.loginColorPrimario) {
          aplicar(d.loginLogoUrl ?? null, d.loginColorPrimario ?? "#6366F1");
          return;
        }

        const sucId = d.sucursales?.[0]?.id;
        if (sucId) {
          fetch(`/api/v1/menu-publico/sucursal/${sucId}/config`)
            .then(r => r.json())
            .then(mc => aplicar(mc.logoUrl ?? null, mc.colorPrimario ?? "#6366F1", mc.imagenFondoUrl ?? null))
            .catch(() => setOrgInfo({ nombre }));
        } else { setOrgInfo({ nombre }); }
      })
      .catch(() => {});

    fetch("/api/v1/reservas/public-stats")
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.pendientes != null && setPendientes(Number(d.pendientes)))
      .catch(() => {});
  }, []);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setServerError("");
    try {
      const res = await api<{ accessToken: string; refreshToken: string; user: AuthUser }>(
        "/v1/auth/login", { method: "POST", body: data },
      );
      setSession({ token: res.accessToken, refreshToken: res.refreshToken ?? "", user: res.user });
      navigate(rutaInicio(res.user), { replace: true });
    } catch (e: any) {
      setServerError(e.message ?? "Credenciales inválidas");
    }
  };

  const accent = orgInfo?.colorPrimario ?? "#6366F1";
  const hasLogo = !!orgInfo?.logoUrl;
  const hasBg   = !!orgInfo?.imagenFondoUrl;
  const año = new Date().getFullYear();

  // Bullets de valor mostrados en el panel hero (sólo desktop/tablet ancho)
  const valueProps = [
    { icon: CalendarCheck, label: "Reservas",     desc: "agenda 24/7 con lista de espera" },
    { icon: Utensils,      label: "Delivery propio", desc: "sin comisiones de terceros" },
    { icon: MessageSquare, label: "WhatsApp",     desc: "confirmaciones automáticas" },
    { icon: BarChart3,     label: "Cliente 360°", desc: "hábitos, ticket y retención" },
    { icon: Wallet,        label: "Pagos",        desc: "verificación y aprobación" },
    { icon: ShieldCheck,   label: "Multi-tenant", desc: "datos aislados por local" },
  ];

  return (
    <div className="lg-root" style={{ ["--accent" as any]: accent }}>
      {/* ─── Fondo dinámico ─── */}
      {hasBg && (
        <div className="lg-bg-img" style={{ backgroundImage: `url(${orgInfo!.imagenFondoUrl})` }} />
      )}
      <div className="lg-bg-overlay" style={{
        background: hasBg
          ? "linear-gradient(135deg,rgba(8,8,14,0.92) 0%,rgba(8,8,14,0.78) 100%)"
          : `radial-gradient(circle at 15% 20%, ${accent}26 0%, transparent 55%),
             radial-gradient(circle at 85% 80%, ${accent}1a 0%, transparent 55%),
             linear-gradient(135deg,#0a0a10 0%,#11111a 100%)`
      }} />
      {/* Grano sutil */}
      <div className="lg-grain" />

      {/* ─── Layout principal: split en desktop ─── */}
      <div className="lg-shell">

        {/* ─── HERO IZQUIERDO (oculto en mobile) ─── */}
        <aside className="lg-hero">
          <div className="lg-hero-inner">
            <div className="lg-hero-mark">
              <Sparkles size={14} />
              <span>CRM RedesIP · {orgInfo?.nombre || "Hospitality OS"}</span>
            </div>

            <h2 className="lg-hero-title">
              Reservas, delivery y clientes <em>en un solo lugar</em>.
            </h2>
            <p className="lg-hero-sub">
              Todo lo que tu restaurante necesita para llenar mesas, vender
              delivery propio y conocer a cada cliente como tu mejor mesero.
            </p>

            <ul className="lg-hero-grid">
              {valueProps.map((vp) => {
                const Icon = vp.icon;
                return (
                  <li key={vp.label} className="lg-hero-item">
                    <span className="lg-hero-ico"><Icon size={15} /></span>
                    <div>
                      <strong>{vp.label}</strong>
                      <span>{vp.desc}</span>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="lg-hero-foot">
              <span className="lg-dot" /> Servidores activos · ICG sync · WhatsApp listo
            </div>
          </div>
        </aside>

        {/* ─── FORMULARIO DERECHO ─── */}
        <main className="lg-pane">
          <div className="lg-card">

            {/* Brand */}
            <header className="lg-brand">
              {hasLogo
                ? <img src={orgInfo!.logoUrl!} alt={orgInfo?.nombre} className="lg-logo" />
                : (
                  <div className="lg-initials">
                    {orgInfo?.nombre?.charAt(0) ?? "C"}
                  </div>
                )
              }
              <h1 className="lg-title">{orgInfo?.nombre || "Bienvenido"}</h1>
              <p className="lg-subtitle">Ingresá para gestionar tu operación</p>
            </header>

            {/* Alerta pendientes */}
            {pendientes !== null && pendientes > 0 && (
              <div className="lg-alert" role="status">
                <span className="lg-alert-ico"><CalendarCheck size={16} /></span>
                <div>
                  <p className="lg-alert-title">
                    {pendientes} reserva{pendientes !== 1 ? "s" : ""} esperando confirmación
                  </p>
                  <p className="lg-alert-sub">Ingresá para gestionarlas ahora</p>
                </div>
              </div>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit(onSubmit)} className="lg-form" noValidate>
              {serverError && (
                <div className="lg-error" role="alert">{serverError}</div>
              )}

              <div className="lg-field">
                <label htmlFor="lg-email" className="lg-label">Email</label>
                <input
                  id="lg-email" {...register("email")}
                  type="email" autoComplete="email"
                  placeholder="tu@email.com"
                  className={`lg-input ${errors.email ? "is-err" : ""}`}
                />
                {errors.email && <p className="lg-field-err">{errors.email.message}</p>}
              </div>

              <div className="lg-field">
                <label htmlFor="lg-pass" className="lg-label">Contraseña</label>
                <div className="lg-input-wrap">
                  <input
                    id="lg-pass" {...register("password")}
                    type={showPass ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={`lg-input lg-input-pass ${errors.password ? "is-err" : ""}`}
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="lg-eye"
                    aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}>
                    {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {errors.password && <p className="lg-field-err">{errors.password.message}</p>}
              </div>

              <button type="submit" disabled={isSubmitting} className="lg-btn">
                {isSubmitting
                  ? <><Loader2 size={18} className="lg-spin" /> Ingresando…</>
                  : <>Iniciar sesión <ArrowRight size={18} className="lg-btn-arrow" /></>
                }
              </button>
            </form>

            {/* Footer */}
            <footer className="lg-foot">
              <a href="https://redesip.org" target="_blank" rel="noreferrer" className="lg-foot-link">
                <img src="/redesip-logo.png" alt="" className="lg-foot-img"
                  onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                <span>Powered by <strong>redesip.org</strong></span>
              </a>
              <span className="lg-foot-sep" aria-hidden>·</span>
              <span className="lg-foot-yr">© {año}</span>
            </footer>
          </div>
        </main>
      </div>

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        .lg-root {
          --accent-soft: color-mix(in srgb, var(--accent) 18%, transparent);
          --accent-ring: color-mix(in srgb, var(--accent) 45%, transparent);
          min-height: 100vh; min-height: 100dvh;
          position: relative; overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #08080c; color: #fff;
        }

        /* Fondos */
        .lg-bg-img {
          position: fixed; inset: 0; z-index: 0;
          background-size: cover; background-position: center;
          filter: blur(10px) brightness(0.32) saturate(0.9);
          transform: scale(1.08);
        }
        .lg-bg-overlay { position: fixed; inset: 0; z-index: 1; pointer-events: none; }
        .lg-grain {
          position: fixed; inset: 0; z-index: 2; pointer-events: none;
          opacity: 0.035; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }

        /* Shell — split en lg, columna en sm */
        .lg-shell {
          position: relative; z-index: 10;
          min-height: 100vh; min-height: 100dvh;
          display: grid; grid-template-columns: 1fr;
          padding: env(safe-area-inset-top, 24px) 18px env(safe-area-inset-bottom, 24px);
        }

        /* ─── Hero (panel izquierdo) ─── */
        .lg-hero { display: none; }
        .lg-hero-inner {
          height: 100%; display: flex; flex-direction: column; justify-content: center;
          padding: 56px 56px 56px 7vw; max-width: 640px;
        }
        .lg-hero-mark {
          display: flex; align-items: center; gap: 8px;
          padding: 6px 12px; border-radius: 999px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-ring);
          color: #fff; font-size: 11px; font-weight: 600;
          letter-spacing: 0.06em; text-transform: uppercase;
          align-self: flex-start; margin-bottom: 26px;
        }
        .lg-hero-mark svg { color: var(--accent); }
        .lg-hero-title {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: clamp(28px, 3.4vw, 44px); line-height: 1.08;
          font-weight: 700; letter-spacing: -0.025em; margin: 0 0 18px;
          color: #fff;
        }
        .lg-hero-title em {
          font-style: italic; font-weight: 500;
          background: linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 60%, #fff) 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .lg-hero-sub {
          color: rgba(255,255,255,0.62); font-size: 15px; line-height: 1.6;
          margin: 0 0 32px; max-width: 480px;
        }
        .lg-hero-grid {
          list-style: none; padding: 0; margin: 0 0 32px;
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px;
        }
        .lg-hero-item {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 14px; border-radius: 12px;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.07);
        }
        .lg-hero-ico {
          flex-shrink: 0; width: 32px; height: 32px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          background: var(--accent-soft); color: var(--accent);
        }
        .lg-hero-item strong {
          display: block; color: #fff; font-size: 13px; font-weight: 600; margin-bottom: 2px;
        }
        .lg-hero-item div span {
          display: block; color: rgba(255,255,255,0.5); font-size: 12px; line-height: 1.4;
        }
        .lg-hero-foot {
          color: rgba(255,255,255,0.42); font-size: 12px;
          display: flex; align-items: center; gap: 8px;
        }
        .lg-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #34d399;
          box-shadow: 0 0 0 4px rgba(52,211,153,0.18);
          animation: lg-pulse 2.4s ease-out infinite;
        }
        @keyframes lg-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(52,211,153,0.18); }
          50% { box-shadow: 0 0 0 8px rgba(52,211,153,0.04); }
        }

        /* ─── Pane derecho (form) ─── */
        .lg-pane {
          display: flex; align-items: center; justify-content: center;
          width: 100%;
        }
        .lg-card {
          width: 100%; max-width: 420px;
          background: rgba(255,255,255,0.045);
          backdrop-filter: blur(28px) saturate(140%);
          -webkit-backdrop-filter: blur(28px) saturate(140%);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 22px;
          padding: 38px 32px 28px;
          box-shadow:
            0 1px 0 0 rgba(255,255,255,0.08) inset,
            0 24px 60px -12px rgba(0,0,0,0.55),
            0 0 0 1px rgba(255,255,255,0.02);
          animation: lg-card-in 0.5s cubic-bezier(.2,.8,.2,1) both;
        }
        @keyframes lg-card-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: none; }
        }

        /* Brand */
        .lg-brand { text-align: center; margin-bottom: 22px; }
        .lg-logo {
          height: 72px; max-width: 200px; object-fit: contain;
          margin: 0 auto 14px; display: block;
          filter: drop-shadow(0 8px 20px rgba(0,0,0,0.4));
          animation: lg-logo-in 0.6s cubic-bezier(.2,.8,.2,1) both 0.05s;
        }
        .lg-initials {
          width: 72px; height: 72px; border-radius: 20px;
          background: linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 60%, #000) 100%);
          color: #fff; font-size: 30px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 14px;
          box-shadow: 0 12px 28px color-mix(in srgb, var(--accent) 35%, transparent);
          animation: lg-logo-in 0.6s cubic-bezier(.2,.8,.2,1) both 0.05s;
        }
        @keyframes lg-logo-in {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: none; }
        }
        .lg-title {
          font-family: 'Outfit', system-ui, sans-serif;
          color: #fff; font-size: clamp(20px, 3.6vw, 24px); font-weight: 700;
          margin: 0 0 6px; letter-spacing: -0.02em;
        }
        .lg-subtitle {
          color: rgba(255,255,255,0.45); font-size: 13px; margin: 0;
          letter-spacing: 0.01em;
        }

        /* Alert */
        .lg-alert {
          background: var(--accent-soft);
          border: 1px solid var(--accent-ring);
          border-radius: 12px;
          padding: 11px 13px; margin-bottom: 18px;
          display: flex; align-items: center; gap: 11px;
          animation: lg-card-in 0.5s ease-out both 0.15s;
        }
        .lg-alert-ico {
          width: 34px; height: 34px; border-radius: 10px;
          background: color-mix(in srgb, var(--accent) 26%, transparent);
          color: var(--accent);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .lg-alert-title {
          color: #fff; font-size: 13px; font-weight: 600; margin: 0 0 2px;
        }
        .lg-alert-sub {
          color: rgba(255,255,255,0.5); font-size: 11px; margin: 0;
        }

        /* Form */
        .lg-form { display: flex; flex-direction: column; gap: 14px; }
        .lg-error {
          background: rgba(239,68,68,0.13);
          border: 1px solid rgba(239,68,68,0.32);
          border-radius: 11px; padding: 11px 14px;
          color: #fca5a5; font-size: 13px; text-align: center;
          animation: lg-shake 0.36s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes lg-shake {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
        .lg-field { display: flex; flex-direction: column; gap: 6px; }
        .lg-label {
          color: rgba(255,255,255,0.55); font-size: 11px; font-weight: 600;
          letter-spacing: 0.08em; text-transform: uppercase;
        }
        .lg-input-wrap { position: relative; }
        .lg-input {
          width: 100%; padding: 13px 16px; border-radius: 12px;
          background: rgba(255,255,255,0.06);
          border: 1.5px solid rgba(255,255,255,0.12);
          color: #fff !important; font-size: 16px;
          font-family: inherit; outline: none;
          transition: border-color .2s, background .2s, box-shadow .2s;
          -webkit-text-fill-color: #fff;
        }
        .lg-input-pass { padding-right: 48px; }
        .lg-input:hover { background: rgba(255,255,255,0.08); }
        .lg-input:focus {
          background: rgba(255,255,255,0.1);
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-soft);
        }
        .lg-input.is-err { border-color: rgba(239,68,68,0.5); }
        .lg-input.is-err:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.18); }
        .lg-input::placeholder { color: rgba(255,255,255,0.28) !important; }
        .lg-input:-webkit-autofill,
        .lg-input:-webkit-autofill:hover,
        .lg-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 100px rgba(20,20,30,0.96) inset !important;
          -webkit-text-fill-color: #fff !important;
          caret-color: #fff; transition: background-color 5000s;
        }
        .lg-field-err { color: #fca5a5; font-size: 11px; margin: 0; }
        .lg-eye {
          position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,0.42);
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          transition: color .15s, background .15s;
        }
        .lg-eye:hover { color: #fff; background: rgba(255,255,255,0.06); }

        .lg-btn {
          margin-top: 8px; padding: 15px 18px;
          border-radius: 13px; border: none;
          background: linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 75%, #000) 100%);
          color: #fff; font-size: 15px; font-weight: 600;
          cursor: pointer; display: inline-flex; align-items: center;
          justify-content: center; gap: 10px;
          min-height: 52px;
          box-shadow:
            0 8px 22px color-mix(in srgb, var(--accent) 38%, transparent),
            0 1px 0 0 rgba(255,255,255,0.18) inset;
          transition: transform .15s ease, box-shadow .15s ease, opacity .2s;
          letter-spacing: 0.01em; font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .lg-btn:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow:
            0 12px 30px color-mix(in srgb, var(--accent) 50%, transparent),
            0 1px 0 0 rgba(255,255,255,0.22) inset;
        }
        .lg-btn:not(:disabled):active { transform: translateY(0) scale(0.99); }
        .lg-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .lg-btn-arrow { transition: transform .2s ease; }
        .lg-btn:not(:disabled):hover .lg-btn-arrow { transform: translateX(3px); }
        .lg-spin { animation: lg-spin 0.9s linear infinite; }
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        /* Footer */
        .lg-foot {
          margin-top: 22px; padding-top: 18px;
          border-top: 1px solid rgba(255,255,255,0.07);
          display: flex; align-items: center; justify-content: center;
          gap: 8px; font-size: 11px; color: rgba(255,255,255,0.4);
        }
        .lg-foot-link {
          display: flex; align-items: center; gap: 6px;
          color: rgba(255,255,255,0.55); text-decoration: none;
          transition: color .2s;
        }
        .lg-foot-link:hover { color: rgba(255,255,255,0.9); }
        .lg-foot-link strong { font-weight: 700; letter-spacing: 0.03em; }
        .lg-foot-img { height: 13px; filter: brightness(0) invert(1); opacity: 0.55; }
        .lg-foot-sep { opacity: 0.4; }

        /* ─── RESPONSIVE ─── */

        /* Desktop ≥ 1024px: split 50/50 */
        @media (min-width: 1024px) {
          .lg-shell {
            grid-template-columns: 1.05fr 1fr;
            padding: 0;
          }
          .lg-hero { display: block; }
          .lg-pane { padding: 40px 32px; }
          .lg-card { max-width: 440px; padding: 44px 38px 32px; }
        }

        /* Pantallas grandes */
        @media (min-width: 1400px) {
          .lg-hero-inner { padding-left: 11vw; }
        }

        /* Móvil pequeño (< 380px) */
        @media (max-width: 380px) {
          .lg-card { padding: 30px 22px 24px; border-radius: 18px; }
          .lg-logo { height: 60px; }
          .lg-initials { width: 60px; height: 60px; font-size: 26px; }
        }

        /* Móvil — sheet desde abajo */
        @media (max-width: 640px) {
          .lg-shell { align-content: end; padding: 0; }
          .lg-pane { padding: 32px 16px env(safe-area-inset-bottom, 24px); }
          .lg-card {
            max-width: 100%;
            border-radius: 24px 24px 18px 18px;
            background: rgba(255,255,255,0.06);
          }
        }

        /* Landscape móvil */
        @media (max-height: 600px) and (orientation: landscape) and (max-width: 1023px) {
          .lg-brand { margin-bottom: 16px; }
          .lg-logo { height: 48px; margin-bottom: 8px; }
          .lg-initials { width: 52px; height: 52px; font-size: 22px; margin-bottom: 8px; }
          .lg-title { font-size: 18px; }
          .lg-card { padding: 22px 26px 18px; }
          .lg-form { gap: 10px; }
          .lg-alert { padding: 8px 11px; margin-bottom: 12px; }
        }

        /* Reduce motion */
        @media (prefers-reduced-motion: reduce) {
          .lg-card, .lg-logo, .lg-initials, .lg-alert, .lg-dot,
          .lg-error, .lg-btn, .lg-btn-arrow, .lg-spin {
            animation: none !important; transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
